import en from './en';
import type { AdminUiCopy } from './types';

type ExtendedSections = Pick<
  AdminUiCopy,
  | 'ownerStaff'
  | 'ownerBlogManager'
  | 'ownerBlogEditor'
  | 'superAdminDashboard'
  | 'superAdminSecurity'
  | 'superAdminUsers'
  | 'superAdminDemoRequests'
  | 'superAdminUserDetail'
>;

const esExtendedSections: ExtendedSections = {
  ownerStaff: {
    ownerTools: 'Herramientas de propietario',
    notAuthorized: 'No autorizado',
    restrictedBody:
      'Esta zona está restringida a propietarios de la plataforma. Las herramientas de propietario controlan el acceso del staff y escriben directamente en permisos de producción.',
    ownerAccessRequired: 'Se requiere acceso de propietario',
    notInOwnerEmails: 'Tu correo {email} no está en PLATFORM_OWNER_EMAILS.',
    addAndReauth:
      'Añádelo ahí y vuelve a autenticarte con SSO de Google/GitHub. En producción, el estado de propietario se evalúa desde la configuración de entorno y la sesión SSO.',
    manageStaffBody:
      'Gestiona acceso de staff (super admin). Los cambios se aplican al instante y se registran para auditoría. Usa herramientas de Super Admin para suspender o reactivar cuentas de clientes.',
    grantRevokeTitle: 'Conceder/Revocar acceso de staff',
    email: 'Correo',
    action: 'Acción',
    reason: 'Motivo',
    grantStaff: 'Conceder staff',
    revokeStaff: 'Revocar staff',
    working: 'Procesando...',
    enterEmail: 'Introduce un correo para continuar',
    provideReason: 'Indica un motivo (para registros de auditoría)',
    currentStaff: 'Staff actual',
    noStaff: 'No hay cuentas de staff.',
    tableEmail: 'Correo',
    tableId: 'ID',
    tableActions: 'Acciones',
    revoke: 'Revocar',
    prev: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {page} de {total}',
    recentActions: 'Acciones recientes del propietario',
    noRecentActions: 'No hay acciones recientes.',
    grantDialogTitle: '¿Conceder acceso de staff?',
    revokeDialogTitle: '¿Revocar acceso de staff?',
    grantDialogDescription: 'Esto concede acceso total a la plataforma a {email}.',
    revokeDialogDescription: 'Esto elimina el acceso de plataforma a {email}.',
    confirmGrant: 'Conceder',
    confirmRevoke: 'Revocar',
    grantSuccess: 'Acceso staff concedido a {email}',
    revokeSuccess: 'Acceso staff revocado para {email}',
    failedUpdateStaff: 'No se pudo actualizar el staff',
    failedRevoke: 'No se pudo revocar',
    revokeReasonPlaceholder: 'Motivo de la revocación',
    revokeReasonHint: 'Se usa para auditoría; sé breve.',
    ownerManagedUpdateDefault: 'Actualización de staff gestionada por propietario',
    accessNoLongerRequiredDefault: 'Acceso ya no requerido',
  },
  ownerBlogManager: {
    title: 'Gestor de blog',
    subtitle: 'Crear y administrar publicaciones del blog',
    accessRestricted: 'Acceso restringido',
    accessBody: 'La gestión del blog requiere acceso de propietario o super admin.',
    newPost: 'Nueva publicación',
    all: 'Todos',
    draft: 'Borrador',
    published: 'Publicado',
    loadingPosts: 'Cargando publicaciones...',
    noPosts: 'Aún no hay publicaciones. Crea tu primera publicación.',
    createFirstPost: 'Crea tu primera publicación para empezar a publicar novedades.',
    createPost: 'Crear publicación',
    tableTitle: 'Título',
    tableStatus: 'Estado',
    tableAuthor: 'Autor',
    tableDate: 'Fecha',
    tableActions: 'Acciones',
    edit: 'Editar',
    view: 'Ver',
    delete: 'Eliminar',
    previous: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {page} de {total}',
    deleteTitle: '¿Eliminar publicación?',
    deleteBody: 'Esta acción no se puede deshacer. La publicación se eliminará permanentemente.',
    cancel: 'Cancelar',
    confirmDelete: 'Eliminar',
    toastPostDeleted: 'Publicación eliminada',
    toastDeleteFailed: 'No se pudo eliminar la publicación',
  },
  ownerBlogEditor: {
    title: 'Editor de blog',
    accessRestricted: 'Acceso restringido',
    accessBody: 'La gestión del blog requiere acceso de propietario o super admin.',
    editPost: 'Editar publicación',
    newPost: 'Nueva publicación',
    backToManager: 'Volver al gestor de blog',
    saveDraft: 'Guardar borrador',
    publish: 'Publicar',
    saving: 'Guardando...',
    publishing: 'Publicando...',
    postTitlePlaceholder: 'Título de la publicación',
    status: 'Estado',
    draft: 'Borrador',
    published: 'Publicado',
    slug: 'Slug URL',
    slugPlaceholder: 'slug-publicacion',
    excerpt: 'Extracto',
    excerptPlaceholder: 'Resumen breve para la lista del blog...',
    featuredImageUrl: 'URL de imagen destacada',
    tags: 'Etiquetas',
    tagsPlaceholder: 'EUDI, Actualización de producto, Ingeniería',
    commaSeparated: 'Separado por comas',
    authorName: 'Nombre del autor',
    authorNamePlaceholder: 'Nombre del autor',
    authorAvatarUrl: 'URL de avatar del autor',
    remove: 'Quitar',
    importMarkdown: 'Importar Markdown',
    markdown: 'Markdown',
    markdownPlaceholder: 'Pega aquí tu markdown...',
    preview: 'Vista previa',
    previewPlaceholder: 'La vista previa aparecerá aquí...',
    cancel: 'Cancelar',
    import: 'Importar',
    titleRequired: 'El título es obligatorio',
    contentRequired: 'El contenido es obligatorio',
    failedLoadPost: 'No se pudo cargar la publicación',
    recoveredDraft: 'Borrador sin guardar recuperado',
    postPublished: 'Publicación publicada',
    draftSaved: 'Borrador guardado',
    draftCreated: 'Borrador creado',
    failedSavePost: 'No se pudo guardar la publicación',
    imageUploaded: 'Imagen subida',
    avatarUploaded: 'Avatar subido',
    uploadFailed: 'Subida fallida',
    markdownImported: 'Markdown importado',
    startWritingPlaceholder: 'Empieza a escribir tu publicación...',
  },
  superAdminDashboard: {
    loadingMetrics: 'Cargando métricas de la plataforma...',
    platformOverview: 'Resumen de plataforma',
    globalMetrics: 'Métricas globales y analítica de rendimiento',
    noMetrics: 'No hay métricas disponibles',
    quickActions: 'Acciones rápidas',
    platformSecurity: 'Seguridad de plataforma',
    viewAllUsers: 'Ver todos los usuarios',
    demoRequests: 'Solicitudes de demo',
    trialUsers: 'Usuarios en prueba',
    suspendedUsers: 'Usuarios suspendidos',
    userMetrics: 'Métricas de usuarios',
    totalUsers: 'Usuarios totales',
    activeUsers: 'Usuarios activos',
    activePaidUsers: 'Usuarios de pago activos',
    mrr: 'MRR',
    monthlyRecurringRevenue: 'Ingresos recurrentes mensuales',
    ofTotal: '{value}% del total',
    ofActiveUsers: '{value}% de los usuarios activos',
    planDistribution: 'Distribución de planes',
    trial: 'Prueba',
    pro: 'Pro',
    business: 'Business',
    activeUsersWithTotal: 'Usuarios activos{totalSuffix}',
    verificationSessions: 'Sesiones de verificación',
    totalSessions: 'Sesiones totales',
    thisMonth: 'Este mes',
    lastMonth: 'Mes pasado',
    growth: 'Crecimiento',
    liveVerifications: 'Verificaciones live',
    allTime: 'Histórico',
    apiKeys: 'Claves API',
    totalKeys: 'Claves totales',
    activeKeys: 'Claves activas',
    liveKeys: 'Claves live',
    productionEnvironment: 'Entorno de producción',
  },
  superAdminSecurity: {
    superAdmin: 'Super Admin',
    platformSecurity: 'Seguridad de plataforma',
    subtitle: 'Resumen de anomalías entre comercios para usuarios de staff.',
    totalAlerts: 'Alertas totales',
    activeAlerts: 'Alertas activas',
    critical: 'Crítica',
    topMerchantsLast7d: 'Top comercios (7 días)',
    filters: 'Filtros',
    severity: 'Severidad',
    status: 'Estado',
    merchantIdOptional: 'ID de comercio (opcional)',
    show: 'Mostrar',
    any: 'Cualquiera',
    low: 'Baja',
    medium: 'Media',
    high: 'Alta',
    active: 'Activa',
    investigating: 'Investigando',
    resolved: 'Resuelta',
    falsePositive: 'Falso positivo',
    refresh: 'Actualizar',
    securityAlerts: 'Alertas de seguridad',
    noAlerts: 'No hay alertas para los filtros actuales.',
    time: 'Hora',
    merchant: 'Comercio',
    type: 'Tipo',
    pageOf: 'Página {page} de {total}',
    prev: 'Anterior',
    next: 'Siguiente',
  },
  superAdminUsers: {
    superAdmin: 'Super Admin',
    allUsers: 'Todos los usuarios',
    subtitle: 'Gestión de usuarios y analítica de toda la plataforma',
    totalUsers: 'Usuarios totales',
    activeUsers: 'Usuarios activos',
    trialUsers: 'Usuarios en prueba',
    paidUsers: 'Usuarios de pago',
    filters: 'Filtros',
    exporting: 'Exportando...',
    exportCsv: 'Exportar CSV',
    hideFilters: 'Ocultar filtros',
    showFilters: 'Mostrar filtros',
    search: 'Buscar',
    searchPlaceholder: 'Correo, nombre o empresa...',
    planTier: 'Nivel de plan',
    allTiers: 'Todos los niveles',
    trial: 'Prueba',
    pro: 'Pro',
    business: 'Business',
    status: 'Estado',
    allStatuses: 'Todos los estados',
    active: 'Activo',
    suspended: 'Suspendido',
    loadingUsers: 'Cargando usuarios...',
    noUsersFound: 'No se encontraron usuarios',
    tableUser: 'Usuario',
    tablePlan: 'Plan',
    tablePlanActive: 'Plan activo',
    tableUsage: 'Uso',
    tableResources: 'Recursos',
    tableCreated: 'Creado',
    tableActions: 'Acciones',
    inactive: 'Inactivo',
    admin: 'Admin',
    liveThisMonth: 'live este mes',
    keys: 'claves',
    sessions: 'sesiones',
    updated: 'Actualizado',
    viewSelf: 'Ver mi cuenta',
    viewDetails: 'Ver detalles',
    usage: 'Uso',
    resources: 'Recursos',
    created: 'Creado',
    showingRange: 'Mostrando {start} a {end} de {total} usuarios',
    previous: 'Anterior',
    next: 'Siguiente',
    exportStarted: 'Exportación CSV iniciada.',
    unableExport: 'No se pudieron exportar los usuarios.',
  },
  superAdminDemoRequests: {
    superAdmin: 'Super Admin',
    demoRequests: 'Solicitudes de demo',
    subtitle: 'Sigue y gestiona solicitudes de demo de clientes potenciales',
    totalRequests: 'Solicitudes totales',
    pending: 'Pendiente',
    contacted: 'Contactado',
    completed: 'Completado',
    allRequests: 'Todas las solicitudes',
    pendingOnly: 'Solo pendientes',
    scheduled: 'Programado',
    onboarded: 'Incorporado',
    followUp: 'Seguimiento',
    churned: 'Perdido',
    showing: 'Mostrando {count} de {total} solicitudes',
    loading: 'Cargando solicitudes de demo...',
    noRequestsFound: 'No se encontraron solicitudes de demo',
    noRequestsAll:
      'Aún no se han enviado solicitudes de demo. Aparecerán aquí cuando los clientes reserven demos desde tu landing page.',
    noRequestsWithStatus: 'No hay solicitudes con estado "{status}". Prueba otro filtro.',
    contact: 'Contacto',
    quickInfo: 'Info rápida',
    status: 'Estado',
    submitted: 'Enviada',
    actions: 'Acciones',
    hideDetails: 'Ocultar detalles',
    showDetails: 'Mostrar detalles',
    noInfo: 'Sin información',
    email: 'Correo',
    role: 'Rol',
    useCase: 'Caso de uso',
    expectedVolume: 'Volumen esperado',
    message: 'Mensaje',
    viewFullDetails: 'Ver todos los detalles',
    pageOf: 'Página {page} de {total}',
    justNow: 'Ahora mismo',
    minutesAgo: 'hace {count} min',
    hoursAgo: 'hace {count} h',
    daysAgo: 'hace {count} d',
    statusUpdated: 'Estado actualizado correctamente',
    statusUpdateFailed: 'No se pudo actualizar el estado',
  },
  superAdminUserDetail: {
    loadingUserDetails: 'Cargando detalles del usuario...',
    backToUsers: 'Volver a usuarios',
    userNotFound: 'Usuario no encontrado',
    superAdmin: 'Super Admin',
    allUsers: 'Todos los usuarios',
    editUser: 'Editar usuario',
    accountActions: 'Acciones de cuenta',
    accountActionsBody:
      'Las cuentas suspendidas no pueden iniciar sesión ni crear nuevas sesiones de verificación.',
    suspending: 'Suspendiendo...',
    suspendAccount: 'Suspender cuenta',
    reactivating: 'Reactivando...',
    reactivateAccount: 'Reactivar cuenta',
    updateUser: 'Actualizar usuario',
    planTier: 'Nivel de plan',
    status: 'Estado',
    free: 'Gratis',
    pro: 'Pro',
    business: 'Business',
    suspended: 'Suspendido',
    saveChanges: 'Guardar cambios',
    saving: 'Guardando...',
    accountInformation: 'Información de la cuenta',
    email: 'Correo',
    name: 'Nombre',
    company: 'Empresa',
    emailVerified: 'Correo verificado',
    verified: 'Verificado',
    notVerified: 'No verificado',
    authProvider: 'Proveedor de auth',
    created: 'Creado',
    lastUpdated: 'Última actualización',
    planDetails: 'Detalles del plan',
    currentPlan: 'Plan actual',
    perMonth: 'mes',
    testVerifications: 'Verificaciones de prueba',
    unlimited: 'Ilimitado',
    liveVerifications: 'Verificaciones live',
    maxTestKeys: 'Máx claves test',
    maxLiveKeys: 'Máx claves live',
    suspendTitle: '¿Suspender esta cuenta?',
    suspendDescription:
      'Los usuarios suspendidos no pueden iniciar sesión ni crear nuevas sesiones de verificación. Los datos existentes se mantienen.',
    suspend: 'Suspender',
    reactivateTitle: '¿Reactivar esta cuenta?',
    reactivateDescription: 'La reactivación restablece el acceso de inmediato.',
    reactivate: 'Reactivar',
    totalSessions: 'Sesiones totales',
    completed: 'Completadas',
    failed: 'Fallidas',
    thisMonth: 'Este mes',
    apiKeys: 'Claves API',
    totalKeys: 'Claves totales',
    activeKeys: 'Claves activas',
    publicId: 'ID pública',
    environment: 'Entorno',
    lastUsed: 'Último uso',
    live: 'Live',
    test: 'Test',
    active: 'Activa',
    revoked: 'Revocada',
    never: 'Nunca',
    showingRange: 'Mostrando {start} a {end} de {total} claves',
    previous: 'Anterior',
    next: 'Siguiente',
    pageOf: 'Página {page} de {total}',
  },
};

const es: AdminUiCopy = {
  ...en,
  publicHeader: {
    features: 'Funciones',
    howItWorks: 'Cómo funciona',
    pricing: 'Precios',
    blog: 'Blog',
    docs: 'Documentación',
    signIn: 'Iniciar sesión',
    getApiKey: 'Obtener API Key',
  },
  login: {
    ...en.login,
    welcomeBack: 'Bienvenido de nuevo',
    subtitle: 'Inicia sesión en tu panel',
    accessRestricted: 'Acceso restringido',
    contactSupport: 'Contactar soporte',
    backToHome: 'Volver al inicio',
    continueWithEmail: 'o continuar con correo',
    password: 'Contraseña',
    forgotPassword: '¿Olvidaste tu contraseña?',
    signIn: 'Iniciar sesión',
    noAccount: '¿Aún no tienes cuenta?',
    createAccount: 'Crear cuenta',
  },
  register: {
    ...en.register,
    createAccount: 'Crear cuenta',
    subtitle: 'Empieza con un plan gratuito - sin tarjeta de crédito',
    fullName: 'Nombre completo',
    companyOptional: 'Empresa (opcional)',
    password: 'Contraseña',
    passwordStrength: 'Seguridad de contraseña',
    creating: 'Creando cuenta…',
    haveAccount: '¿Ya tienes una cuenta?',
    signIn: 'Iniciar sesión',
    teamInviteAcceptedTitle: 'Invitación de equipo aceptada',
    teamInviteAcceptedBody: 'Te añadimos al equipo. Redirigiendo a tu panel de equipo...',
    inviteLockedEmailHint: 'Este correo está vinculado a tu invitación de equipo y no se puede cambiar.',
  },
  forgotPassword: {
    ...en.forgotPassword,
    checkEmail: 'Revisa tu correo',
    accountExistsNoticeSuffix: 'recibirás en breve un enlace para restablecer la contraseña.',
    title: '¿Olvidaste tu contraseña?',
    subtitle: 'Escribe tu correo y te enviaremos un enlace de restablecimiento',
    sendResetLink: 'Enviar enlace',
    rememberPassword: '¿Recuerdas tu contraseña?',
    signIn: 'Iniciar sesión',
  },
  resetPassword: {
    ...en.resetPassword,
    successTitle: 'Contraseña restablecida',
    successBody: 'Tu contraseña se restableció correctamente.',
    successCta: 'Iniciar sesión ahora',
    title: 'Restablecer contraseña',
    subtitle: 'Introduce tu nueva contraseña',
    newPassword: 'Nueva contraseña',
    confirmPassword: 'Confirmar contraseña',
    passwordStrength: 'Seguridad de contraseña',
    reset: 'Restablecer contraseña',
    rememberPassword: '¿Recuerdas tu contraseña?',
    signIn: 'Iniciar sesión',
  },
  notFound: {
    pageNotFound: 'Página no encontrada',
    description: 'La página que buscas no existe o fue movida.',
    goBack: 'Volver',
    goDashboard: 'Ir al panel',
    supportPrefix: 'Si crees que esto es un error, contacta con',
  },
  teamManagement: {
    title: 'Equipo',
    subtitleWithCount: '{current} de {max} miembros',
    subtitleDefault: 'Gestiona los miembros de tu equipo',
    leaveTeam: 'Salir del equipo',
    planLockedTitle: 'La gestión de equipos está disponible en planes de pago',
    planLockedBody: 'Actualiza a Pro para invitar hasta 3 miembros, o a Business para hasta 10.',
    viewPlans: 'Ver planes',
    inviteMemberTitle: 'Invitar a un miembro',
    inviteCopiedTitle: 'Enlace de invitación copiado',
    copyAgain: 'Copiar otra vez',
    inviteLinkHint: 'Comparte este enlace con tu miembro. Caduca en 7 días.',
    invitePlaceholder: 'colega@empresa.com',
    roleAdmin: 'Admin',
    roleDeveloper: 'Desarrollador',
    roleViewer: 'Lector',
    roleAdminDescription: 'Acceso total',
    roleDeveloperDescription: 'Gestionar claves y playground',
    roleViewerDescription: 'Acceso de solo lectura',
    sendInvite: 'Enviar invitación',
    sendingInvite: 'Enviando...',
    limitReachedPrefix: 'Se alcanzó el límite de miembros.',
    upgradePlan: 'Mejorar plan',
    membersTitle: 'Miembros',
    owner: 'Propietario',
    ownerDescription: 'Propietario de la cuenta',
    joinedOn: 'Se unió el {date}',
    pending: 'Pendiente',
    remove: 'Quitar',
    noMembers: 'Aún no hay miembros.',
    noMembersCta: 'Envía una invitación para empezar.',
    pendingInvitesTitle: 'Invitaciones pendientes',
    expires: 'Caduca {date}',
    copyLink: 'Copiar enlace',
    revoke: 'Revocar',
    leaveDialogTitle: 'Salir del equipo',
    leaveDialogBody:
      '¿Seguro que quieres salir de este equipo? Perderás acceso a los recursos de esta cuenta y necesitarás una nueva invitación para volver.',
    leaveDialogConfirm: 'Salir del equipo',
    removeDialogTitle: 'Quitar miembro',
    removeDialogBody: '¿Quitar a {email} del equipo? Perderá el acceso inmediatamente.',
    removeDialogConfirm: 'Quitar',
    revokeDialogTitle: '¿Revocar invitación?',
    revokeDialogBody: 'Se cancelará la invitación a {email}. No podrá unirse al equipo.',
    toastInviteCopied: 'Enlace de invitación copiado',
    toastCopiedAgain: 'Copiado otra vez',
    toastInviteRevoked: 'Invitación revocada',
    toastInviteRevokeFailed: 'No se pudo revocar la invitación',
    toastMemberRemoved: 'Miembro eliminado',
    toastMemberRemoveFailed: 'No se pudo eliminar al miembro',
    toastRoleUpdated: 'Rol actualizado',
    toastRoleUpdateFailed: 'No se pudo actualizar el rol',
    toastLeftTeam: 'Has salido del equipo',
    toastLeaveTeamFailed: 'No se pudo salir del equipo',
    inviteSendFailed: 'No se pudo enviar la invitación',
  },
  auditLog: {
    title: 'Registro de auditoría',
    subtitle: 'Sigue toda la actividad de tu cuenta.',
    searchPlaceholder: 'Buscar por acción, actor, objetivo, IP, ID de solicitud o ID de evento',
    allCategories: 'Todas las categorías',
    categorySecurity: 'Seguridad',
    categoryApi: 'API',
    categoryDataAccess: 'Acceso a datos',
    categoryConfiguration: 'Configuración',
    allSeverities: 'Todas las severidades',
    severityInfo: 'Info',
    severityWarning: 'Advertencia',
    severityError: 'Error',
    severityCritical: 'Crítica',
    clearFilters: 'Limpiar filtros',
    loading: 'Cargando registros de auditoría...',
    empty: 'No se encontraron entradas de auditoría.',
    noMatches: 'No hay entradas coincidentes en esta página.',
    timestamp: 'Fecha y hora',
    eventKey: 'Clave de evento',
    action: 'Acción',
    actor: 'Actor',
    target: 'Objetivo',
    category: 'Categoría',
    severity: 'Severidad',
    source: 'Origen',
    ipAddress: 'Dirección IP',
    eventId: 'ID de evento',
    requestId: 'ID de solicitud',
    userAgent: 'Agente de usuario',
    unknown: 'Desconocido',
    system: 'Sistema',
    pageSummary: 'Página {page} de {totalPages} ({total} entradas)',
    previous: 'Anterior',
    next: 'Siguiente',
    tapToExpand: 'Toca para ver detalles',
    details: 'Detalles',
  },
  inviteAccept: {
    loading: 'Cargando detalles de la invitación...',
    noToken: 'No se proporcionó token de invitación.',
    invalidInviteDefault: 'Esta invitación es inválida, caducó o ya se usó.',
    acceptFailedDefault: 'No se pudo aceptar la invitación. Puede haber caducado o sido revocada.',
    inviteTitle: 'Te han invitado',
    inviteSubtitleNeedsLogin: 'Inicia sesión o crea una cuenta para unirte',
    organization: 'Organización',
    invitedBy: 'Invitado por',
    yourRole: 'Tu rol',
    role: 'Rol',
    acceptInvite: 'Aceptar invitación',
    joiningTeam: 'Uniéndote al equipo...',
    successTitle: 'Listo',
    successBodyWithOrg: 'Te uniste a {organization}. Redirigiendo...',
    successBodyDefault: 'Te uniste al equipo. Redirigiendo...',
    errorTitle: 'No se pudo aceptar la invitación',
    goLogin: 'Ir a iniciar sesión',
    signInToAccept: 'Iniciar sesión para aceptar',
    createAccount: 'Crear una cuenta',
    signIn: 'Iniciar sesión',
    needsRegisterTitle: 'Crea tu cuenta',
    needsRegisterBodyWithOrg: 'Crea una cuenta con {email} para unirte a {organization}.',
    needsRegisterBodyDefault: 'Crea una cuenta con {email} para unirte al equipo.',
    roleAdmin: 'Admin',
    roleDeveloper: 'Desarrollador',
    roleViewer: 'Lector',
    roleAdminDescription: 'Acceso total para gestionar claves, webhooks, facturación y equipo',
    roleDeveloperDescription: 'Crear y gestionar claves API, usar el playground',
    roleViewerDescription: 'Acceso de solo lectura a facturación y auditoría',
  },
  keys: {
    ...en.keys,
    title: 'Claves API',
    createKey: '+ Crear clave',
    filters: 'Filtros',
    hideFilters: 'Ocultar filtros',
    showFilters: 'Mostrar filtros',
    emptyAll: 'Aún no hay claves API.',
    unnamed: 'Sin nombre',
    rotate: 'Rotar',
    revoke: 'Revocar',
  },
  webhooks: {
    ...en.webhooks,
    title: 'Webhooks',
    createTitle: 'Crear webhook',
    createWebhook: 'Crear webhook',
    existingTitle: 'Webhooks existentes',
    inspectorTitle: 'Inspector de webhooks',
  },
  billing: {
    ...en.billing,
    title: 'Planes y facturación',
    subtitle: 'Elige el plan que mejor se ajuste a tus necesidades. Actualiza cuando quieras.',
    manageBilling: 'Gestionar facturación',
    currentPlan: 'Plan actual',
    freeFeatures: [
      'Verificaciones de prueba ilimitadas',
      '100 verificaciones live/mes',
      '5 claves de test, 2 claves live',
      'Sandbox básico (aprobar/fallar)',
      'Fragmentos de integración (10 lenguajes)',
      'Soporte por email',
    ],
    proFeatures: [
      'Verificaciones de prueba ilimitadas',
      '1.000 verificaciones live/mes',
      '10 claves de test, 5 claves live',
      'Hasta 3 miembros del equipo',
      '20+ escenarios de prueba predefinidos',
      'Inspector de webhooks',
      'SLA 99,9 %',
      'Soporte por email',
    ],
    businessFeatures: [
      'Verificaciones de prueba ilimitadas',
      '10.000 verificaciones live/mes',
      '10 claves de test, 10 claves live',
      'Hasta 10 miembros del equipo',
      'Todo lo incluido en Pro',
      'Crea tus propios escenarios',
      'Informe de preparación EUDI (export de cumplimiento)',
      'Simulación por país (DE, FR, ES, IT, NL)',
      'Analítica avanzada',
      'Soporte prioritario por email',
    ],
    billingCycle: 'Ciclo de facturación',
    mostPopular: 'Más popular',
    activePlan: 'Plan activo',
    contactSales: 'Contactar ventas',
  },
  dashboard: {
    ...en.dashboard,
    overview: 'Resumen',
    noPriorData: 'Sin datos previos',
    noChange: 'Sin cambios',
    vsLastPeriod: '{value}% respecto al periodo anterior',
    last24Hours: 'Últimas 24 horas',
    last30Days: 'Últimos 30 días',
    last7Days: 'Últimos 7 días',
    hour: 'hora',
    day: 'día',
    totalVerifications: 'Verificaciones totales',
    successRate: 'Tasa de éxito',
    activeSessions: 'Sesiones activas',
    currentlyInProgress: 'Actualmente en curso',
    verificationTrend: 'Tendencia de verificación',
    trackVerificationVolume: 'Sigue el volumen de verificación a lo largo del tiempo',
    total: 'Total',
    completed: 'Completadas',
    failed: 'Fallidas',
    average: 'Promedio',
    peak: 'Pico',
    perBucket: 'Por {bucket}',
    avgPerBucket: 'Promedio por {bucket}',
    noDataYet: 'Aún sin datos',
    verificationsWillAppear: 'Las verificaciones aparecerán aquí cuando comiences',
    currentPlan: 'Plan actual',
    manage: 'Gestionar',
    staff: 'Staff',
    internalAccount: 'Cuenta interna',
    noBillingForStaff: 'Sin facturación para staff',
    liveVerificationsThisMonth: 'Verificaciones live (este mes)',
    upgradeForLiveAccess: 'Actualiza para acceso live',
    nextBillDate: 'Próxima fecha de facturación',
    status: 'Estado',
    daysRemaining: '{days} días restantes',
    renewingSoon: 'Renovación próxima',
    gracePeriod: 'Periodo de gracia',
    daysToUpdatePayment: '{days} días para actualizar el pago',
    active: 'Activo',
    allSystemsOperational: 'Todos los sistemas operativos',
    advancedAnalytics: 'Analítica avanzada',
    detailedBreakdown: 'Desglose detallado por entorno y clave API',
    exportCsv: 'Exportar CSV',
    byEnvironment: 'Por entorno',
    byStatus: 'Por estado',
    live: 'Live',
    test: 'Test',
    topApiKeysThisMonth: 'Top claves API (este mes)',
    advancedAnalyticsUpgrade: 'Analítica avanzada. Actualiza para desgloses detallados y exportación CSV.',
    upgrade: 'Actualizar',
    developerResources: 'Recursos para desarrolladores',
    documentation: 'Documentación',
    typescriptSdk: 'SDK TypeScript',
    onboardingWelcome: 'Bienvenido a WalletGate',
    youreAllSet: '¡Todo listo!',
    accountReady: 'Tu cuenta está lista. Completa tu primera verificación en menos de 5 minutos.',
    getStartedIn3Steps: 'Empieza en 3 pasos',
    getYourApiKey: 'Obtén tu clave API',
    apiKeyReady: 'Ya tienes una clave API lista para usar',
    createFirstApiKey: 'Crea tu primera clave API para autenticar solicitudes',
    viewApiKeys: 'Ver claves API',
    createApiKey: 'Crear clave API',
    installSdk: 'Instalar el SDK',
    addWalletGate: 'Añade WalletGate a tu proyecto con un solo comando',
    copyCommand: 'Copiar comando',
    copied: '¡Copiado!',
    makeFirstRequest: 'Haz tu primera solicitud',
    startVerifying: 'Empieza a verificar identidades digitales con unas pocas líneas de código',
    viewDocumentation: 'Ver documentación',
    quickStartExample: 'Ejemplo de inicio rápido',
    copyCode: 'Copiar código',
    apiDocumentation: 'Documentación API',
    apiDocumentationDesc: 'Referencia API completa y guías',
    codeExamples: 'Ejemplos de código',
    codeExamplesDesc: 'Integraciones de ejemplo en varios lenguajes',
    support: 'Soporte',
    supportDesc: 'Obtén ayuda de nuestro equipo',
    community: 'Comunidad',
    communityDesc: 'Únete a otros desarrolladores',
    complete: 'Completado',
    noPriorRange: 'Sin datos para el {range} anterior',
    noChangeVsPrior: 'Sin cambios respecto al {range} anterior',
    vsPriorRange: '{value}% respecto al {range} anterior',
  },
  playground: {
    ...en.playground,
    export: 'Exportar',
    quickTour: 'Tour rápido',
    quickStart: 'Inicio rápido',
    onboardingConfigureTitle: 'Configura comprobaciones',
    onboardingConfigureMessage: 'Elige una plantilla rápida o añade comprobaciones y luego inicia la verificación.',
    onboardingFlowTitle: 'Sigue el flujo en vivo',
    onboardingFlowMessage: 'Sigue cada hito de la sesión desde handoff hasta decisión.',
    onboardingShipTitle: 'Lanza más rápido',
    onboardingShipMessage: 'Exporta esta ejecución en JSON o copia el código de integración.',
    configureNow: 'Configurar ahora',
    checksPass: '{passed}/{total} comprobaciones aprobadas',
    runsUsed: '{used} / {total} ejecuciones usadas',
  },
  notifications: {
    title: 'Notificaciones',
    subtitle: 'Mantente informado sobre la actividad de la cuenta, alertas de seguridad y actualizaciones del equipo.',
    searchPlaceholder: 'Buscar notificaciones...',
    allCategories: 'Todas las categorías',
    categorySecurity: 'Seguridad',
    categoryBilling: 'Facturación',
    categoryApi: 'API',
    categoryTeam: 'Equipo',
    allStatus: 'Todas',
    unreadOnly: 'No leídas',
    readOnly: 'Leídas',
    clearFilters: 'Limpiar filtros',
    loading: 'Cargando notificaciones...',
    empty: 'Aún no hay notificaciones. La actividad aparecerá aquí cuando ocurra.',
    noMatches: 'Ninguna notificación coincide con tus filtros.',
    markRead: 'Marcar como leída',
    markAllRead: 'Marcar todo como leído',
    delete: 'Eliminar',
    viewAll: 'Ver todas las notificaciones',
    preferences: 'Preferencias',
    preferencesTitle: 'Preferencias de notificación',
    preferencesSubtitle: 'Elige cómo quieres ser notificado para cada categoría.',
    inApp: 'En la app',
    email: 'Correo',
    savePreferences: 'Guardar preferencias',
    savedPreferences: 'Preferencias de notificación guardadas.',
    failedSavePreferences: 'No se pudieron guardar las preferencias de notificación.',
    category: 'Categoría',
    severity: 'Severidad',
    timestamp: 'Hora',
    pageSummary: 'Página {page} de {total}',
    previous: 'Anterior',
    next: 'Siguiente',
    bellNoNotifications: 'Sin notificaciones nuevas',
    bellRecentTitle: 'Notificaciones',
    severityInfo: 'Info',
    severityWarning: 'Advertencia',
    severityError: 'Error',
    severityCritical: 'Crítica',
    toastMarkedRead: 'Notificación marcada como leída.',
    toastMarkedAllRead: 'Todas las notificaciones marcadas como leídas.',
    toastDeleted: 'Notificación eliminada.',
    toastDeleteFailed: 'No se pudo eliminar la notificación.',
    toastNewSingle: 'Tienes 1 notificación nueva.',
    toastNewMultiple: 'Tienes {count} notificaciones nuevas.',
  },
  ...esExtendedSections,
};

export default es;
